import { useState, FormEvent, useEffect, useRef } from 'react';

/**
 * Props for the NewsletterModal component.
 * When using plain JavaScript we omit TypeScript type annotations
 * and rely on runtime checks / PropTypes if needed.
 */
export default function NewsletterModal({ title, description, formType, onClose, formUrl: formUrlProp }: {
  title: string;
  description: string;
  formType: string;
  onClose: () => void;
  formUrl?: string;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle' | 'success' | 'error'
  const [formUrl, setFormUrl] = useState(formUrlProp || '');
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  // -------------------------------------------------------------------------
  // Fetch the URL of the remote form once the component mounts or `formType`
  // changes. Skip if formUrl was provided via props.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (formUrlProp) {
      setFormUrl(formUrlProp);
      return;
    }
    fetch(`https://readdy.ai/api/form/submit-addr?formName=mever-${formType}`)
      .then((res) => res.text())
      .then((url) => setFormUrl(url))
      .catch((err) => {
        console.error('Failed to get form URL:', err);
      });
  }, [formType, formUrlProp]);

  // -------------------------------------------------------------------------
  // Accessibility: focus‑trap & Escape‑key handling.
  // The close button receives initial focus, and body scrolling is locked
  // while the modal is open.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // -------------------------------------------------------------------------
  // Form submission handler.
  // Includes defensive checks and comprehensive error handling.
  // -------------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard clauses – ensure required data is present before proceeding.
    if (!email || !name) {
      setSubmitStatus('error');
      return;
    }
    if (!formUrl) {
      console.warn('Form URL not yet loaded; submission aborted.');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('name', name);
      formData.append('type', formType);

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Auto‑close after a short delay so the user sees the success message.
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        // Try to read any error message from the response for better logging.
        const errorText = await response.text();
        console.error('Form submission failed:', response.status, errorText);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg max-w-md w-full p-6 sm:p-8 relative"
      >
        <button
          ref={closeButtonRef}
          onClick={onClose}
          className="absolute top-4 sm:top-6 right-4 sm:right-6 w-8 h-8 flex items-center justify-center text-black/40 hover:text-black transition-colors cursor-pointer"
          aria-label="モーダルを閉じる"
        >
          <i className="ri-close-line text-2xl" aria-hidden="true"></i>
        </button>

        <h3
          id="modal-title"
          className="text-xl sm:text-2xl font-light text-black mb-2 tracking-wide pr-8"
        >
          {title}
        </h3>
        <p className="text-xs sm:text-sm font-light text-black/60 mb-6 sm:mb-8">
          {description}
        </p>

        <form
          id={`mever-${formType}-form`}
          data-readdy-form
          onSubmit={handleSubmit}
          className="space-y-4 sm:space-y-6"
        >
          <div>
            <label
              htmlFor={`name-${formType}`}
              className="block text-xs sm:text-sm font-light text-black/80 mb-1.5 sm:mb-2"
            >
              お名前{' '}
              <span className="text-rose-500" aria-hidden="true">
                *
              </span>
              <span className="sr-only">（必須）</span>
            </label>
            <input
              type="text"
              id={`name-${formType}`}
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm border border-black/20 rounded-md focus:outline-none focus:border-[#8E7AB5] focus:ring-2 focus:ring-[#8E7AB5]/20 transition-colors"
              placeholder="山田太郎"
              aria-required="true"
            />
          </div>

          <div>
            <label
              htmlFor={`email-${formType}`}
              className="block text-xs sm:text-sm font-light text-black/80 mb-1.5 sm:mb-2"
            >
              メールアドレス{' '}
              <span className="text-rose-500" aria-hidden="true">
                *
              </span>
              <span className="sr-only">（必須）</span>
            </label>
            <input
              type="email"
              id={`email-${formType}`}
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm border border-black/20 rounded-md focus:outline-none focus:border-[#8E7AB5] focus:ring-2 focus:ring-[#8E7AB5]/20 transition-colors"
              placeholder="example@email.com"
              aria-required="true"
            />
          </div>

          {submitStatus === 'success' && (
            <div
              className="p-3 sm:p-4 bg-green-50 text-green-800 text-xs sm:text-sm rounded-md"
              role="alert"
            >
              登録ありがとうございます
            </div>
          )}

          {submitStatus === 'error' && (
            <div
              className="p-3 sm:p-4 bg-red-50 text-red-800 text-xs sm:text-sm rounded-md"
              role="alert"
            >
              エラーが発生しました。もう一度お試しください。
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !formUrl}
            className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-[#8E7AB5] text-white text-sm sm:text-base font-light tracking-wide hover:bg-[#7A6A9E] transition-colors whitespace-nowrap rounded-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            aria-busy={isSubmitting}
          >
            {isSubmitting ? '送信中...' : '登録する'}
          </button>
        </form>
      </div>
    </div>
  );
}
